import React, { useState, useEffect } from 'react';
import SimulatorForm from './components/SimulatorForm';
import ResultsDashboard from './components/ResultsDashboard';
import { api } from './services/api';
import './App.css';

function App() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [economicData, setEconomicData] = useState(null);

  // Fetch current economic data on mount
  useEffect(() => {
    const fetchEconomicData = async () => {
      try {
        const data = await api.getEconomicData();
        setEconomicData(data);
      } catch (err) {
        console.error('Failed to fetch economic data:', err);
      }
    };
    fetchEconomicData();
  }, []);

  // Auto-scroll when results update
  useEffect(() => {
    if (results) {
      setTimeout(() => {
        const resultsElement = document.getElementById('results-section');
        if (resultsElement) {
          resultsElement.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  }, [results]);

  const handleSimulation = async (userProfile) => {
    setLoading(true);
    setError(null);
    setResults(null); // Clear previous results first
    
    try {
      const simulationResults = await api.runInflationSimulation(userProfile);
      setResults(simulationResults);
      // Scroll happens automatically via useEffect
    } catch (err) {
      setError('Failed to run simulation. Please check your inputs and try again.');
      console.error('Simulation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRunDemo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const demoResults = await api.getDemoSimulation();
      setResults(demoResults);
      // Scroll happens automatically via useEffect
    } catch (err) {
      setError('Failed to run demo simulation.');
      console.error('Demo error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      {/* Header */}
      <header className="app-header">
        <div className="container">
          <h1>
            <img src="/logo.png" alt="Econolyze Logo" className="header-logo" />
            <span>Econolyze</span>
          </h1>
          <p className="tagline">
            Analyze how the economy impacts you
          </p>
          
          {economicData && economicData.success && (
            <>
              <div className="economic-indicators">
                <div className="indicator">
                  <span className="indicator-label">Inflation</span>
                  <span className="indicator-value">
                    {economicData.data.inflation.inflation_rate}%
                  </span>
                </div>
                <div className="indicator">
                  <span className="indicator-label">Interest Rate</span>
                  <span className="indicator-value">
                    {economicData.data.interest_rate.current_rate}%
                  </span>
                </div>
                <div className="indicator">
                  <span className="indicator-label">Unemployment</span>
                  <span className="indicator-value">
                    {economicData.data.unemployment.unemployment_rate}%
                  </span>
                </div>
              </div>
              <div className="data-source-badge">
                <span className="badge-icon">📊</span>
                <span className="badge-text">
                  Real-time data from FRED (Federal Reserve Economic Data)
                </span>
                {economicData.data.inflation.data_source === 'mock' && (
                  <span className="badge-demo"> • Demo Mode</span>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container">
        <section className="input-section">
          <div className="section-header">
            <h2>Enter Your Financial Profile</h2>
          </div>
          
          <SimulatorForm onSubmit={handleSimulation} loading={loading} />
          
          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}
        </section>

        {/* Results Section */}
        {results && (
          <section id="results-section" className="results-section">
            <ResultsDashboard results={results} />
          </section>
        )}

        {/* Empty State */}
        {!results && !loading && (
          <section className="empty-state">
            <div className="empty-state-content">
              <span className="empty-icon">📈</span>
              <h3>Ready to see your financial future?</h3>
              <p>Enter your information above and click "Run Simulation"</p>
              <p>or try the demo to see how it works!</p>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="container">
          <p>
            <strong>Econolyze</strong> • Data powered by FRED (Federal Reserve Economic Data) • 
            Inflation calculations use category-specific multipliers • 
            This is a simulation tool for educational purposes
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
